import config from "../Config/config.json";

// Idle timeout and token expiry. Last activity lives in localStorage so it survives
// page reloads / reopened tabs and is shared by every open tab.

export const SESSION_TIMEOUT_MS = (Number(config.sessionTimeOut) || 20) * 60 * 1000;

const LAST_ACTIVITY_KEY = "lastActivity";
const EXPIRED_FLAG_KEY = "sessionExpiredReason";
const ACTIVITY_WRITE_INTERVAL_MS = 5000;

let lastWrite = 0;
let ending = false;

export const markActivity = (force = false) => {
  const now = Date.now();
  // Activity events fire constantly; storing every few seconds is enough
  if (!force && now - lastWrite < ACTIVITY_WRITE_INTERVAL_MS) return;
  lastWrite = now;
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
  } catch {
    // storage unavailable: the idle check falls back to "active"
  }
};

const getLastActivity = () => {
  const value = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
  return Number.isFinite(value) && value > 0 ? value : null;
};

// Expiry time (ms) from a JWT's "exp" claim; null if the token isn't a readable JWT
export const getTokenExpiry = (token: string | null): number | null => {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.exp === "number" ? json.exp * 1000 : null;
  } catch {
    return null;
  }
};

export type SessionEndReason = "idle" | "expired";

// Why the current session should end, or null if it is still valid
export const getSessionEndReason = (): SessionEndReason | null => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  const expiry = getTokenExpiry(token);
  if (expiry !== null && Date.now() >= expiry) return "expired";
  const lastActivity = getLastActivity();
  if (lastActivity !== null && Date.now() - lastActivity > SESSION_TIMEOUT_MS) return "idle";
  return null;
};

// Clears the session and sends the user to the login page (once, even if called repeatedly)
export const endSession = (reason: SessionEndReason) => {
  if (ending) return;
  ending = true;
  try {
    // Best effort, like the Logout page; keepalive lets it finish after the redirect
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("UserId");
    if (token && userId && reason === "idle") {
      fetch(`${config.apiBaseUrl}Auth/logout/${userId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: "{}",
        keepalive: true,
      }).catch(() => undefined);
    }
    localStorage.clear();
    sessionStorage.setItem(EXPIRED_FLAG_KEY, reason);
  } catch {
    // ignore storage errors; navigation below still logs the user out of this tab
  }
  window.location.replace("/login");
};

// Read once by the login page to explain why the user was logged out
export const takeSessionEndReason = (): SessionEndReason | null => {
  try {
    const reason = sessionStorage.getItem(EXPIRED_FLAG_KEY) as SessionEndReason | null;
    sessionStorage.removeItem(EXPIRED_FLAG_KEY);
    return reason;
  } catch {
    return null;
  }
};
